/* eslint-disable react/display-name */
import React, {
  useEffect,
  useRef,
  useState,
  useContext,
  Fragment,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import { useQuery } from 'react-apollo';
import ReactSVG from 'react-svg';
import upload_image from 'upload.png';
import upload_alt from 'upload_alt.svg';
import heart_path from 'heart_path.svg';
import heart_path_image from 'heart_path.png';
import heart_filled_image from 'heart_filled.png';
import heart_2d from 'heart_2d.svg';
import home from 'home.svg';
import bed from 'bed.svg';
import shower from 'shower.svg';
import sqft from 'sqft.svg';
import close from 'times_white.svg';
import close_image from 'times.png';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'react-toastify';
import { disableBodyScroll } from 'body-scroll-lock';
import { GET_MLS_LISTING, userQuery } from '../../constants/queries';
import mls_statuses from '../../constants/mls_statuses.json';
import mls_categories from '../../constants/mls_categories.json';
import { client, clientNoVarnish } from '../client';
import ImageShowcase from './ImageShowcase';
import MLSShowMap from './MLSShowMap';
import DescriptionTruncate from './DescriptionTruncate';
import AgentContact from './AgentContact';
import InquiryForm from './InquiryForm';
import ShareModal from '../ShareModal';
import formatCurrency from '../../utils/formatCurrency';
import { handleHeartAnimation } from '../../utils/handleHeartAnimation';
import FavoriteSuccessBanner from '../CustomToast/FavoriteSuccessBanner';
import LoadingCircle from '../Loading/LoadingCircle';
import ListMyHome from './ListMyHome';
import MLSBreadcrumbs from './MLSBreadcrumbs';
import useWindowSize from '../../hooks/useWindowSize';
import { MLSMapContext } from '../../context/MLSMapContext';
import AdBlock from '../AdBlock';

export default function MLSShow({
  listing_id,
  isModal,
  closeModal,
  areas,
  showTwo,
}) {
  const heartRef = useRef(null);

  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const { typeFilter, setCurrentListing, placards } = useContext(MLSMapContext);
  const isRental =
    typeFilter.label === 'Rental' ||
    typeFilter.label === 'Commercial for Lease';

  const [favorited, setFavorited] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = '//cdn.bc0a.com/autopilot/f00000000166423/autopilot_sdk.js';
    script.id = 'autopilot';
    document.body.appendChild(script);

    return function removeScript() {
      return document.getElementById('autopilot')?.remove();
    };
  }, []);

  const { data, loading } = useQuery(GET_MLS_LISTING, {
    variables: {
      id: listing_id,
    },
    client,
  });

  const { data: userData, loading: userLoading } = useQuery(userQuery, {
    fetchPolicy: 'no-cache',
    client: clientNoVarnish,
  });

  const pushDataInToDataLayer = useCallback(() => {
    if (window.dataLayer && data) {
      const mapPageDataIndex = window.dataLayer.findIndex(
        item => item.content_ids
      );
      // remove the map page data from data layer when the user is on mls show page
      if (mapPageDataIndex > -1) window.dataLayer.splice(mapPageDataIndex, 1);

      window.dataLayer.push({
        content_id: data.mls_listing.id,
        city: data.mls_listing.city,
        region: 'Florida',
        price: data.mls_listing.list_price,
      });
    }
  }, [data]);

  useEffect(() => {
    pushDataInToDataLayer();
  }, [data, pushDataInToDataLayer]);

  useEffect(() => {
    if (data?.mls_listing) {
      setFavorited(data?.mls_listing?.favorited);
      setCurrentListing(data?.mls_listing);
    }
  }, [data, setCurrentListing]);

  if (loading || userLoading)
    return (
      <div className="mls-loading-wrapper">
        <LoadingCircle />
      </div>
    );

  const handleFavorite = () => {
    const tokenElement = document.querySelector('[name=csrf-token]');
    if (userData?.user?.id) {
      axios
        .patch(
          window.Routes.favorite_mls_listing_path(data?.mls_listing),
          null,
          {
            headers: {
              'X-CSRF-Token': tokenElement?.content || '',
              Accept: 'application/json',
            },
          }
        )
        .then(res => {
          handleHeartAnimation({
            ref: heartRef,
            isFavorited: favorited,
          });
          if (res.status === 200) {
            toast.dismiss();
            if (!favorited)
              toast(
                <FavoriteSuccessBanner favorited type={data?.mls_listing} />
              );
            else toast(<FavoriteSuccessBanner type={data?.mls_listing} />);
            setFavorited(!favorited);
          }
        })
        .catch(err => {
          console.error(err);
          if (err.response.status === 401) {
            window.location.pathname = window.Routes.login_path();
          }
        });
    } else {
      const modal = document.querySelector('#popup-modal');
      modal.classList.remove('fadeOut');
      modal.classList.remove('hidden');
      modal.classList.add('fadeIn');
      disableBodyScroll(modal);
    }
  };

  const subType = mls_categories?.find(
    c => c.value === data?.mls_listing?.sub_type
  );

  const shortAddress = () => {
    let str = data.mls_listing.street_num || 'N/A';
    str += data.mls_listing.street_direction
      ? ` ${data.mls_listing.street_direction}`
      : '';
    str += data.mls_listing.street_name
      ? ` ${data.mls_listing.street_name}`
      : '';
    str += data.mls_listing.street_suffix
      ? ` ${data.mls_listing.street_suffix}`
      : '';
    return str;
  };

  return (
    <Fragment>
      <div className="mls-show-page-container">
        <section className="mls-images-wrapper">
          {!isMobile && <MLSBreadcrumbs listing={data?.mls_listing} />}
          <ImageShowcase images={data?.mls_listing?.images} />
          <div className="mls-actions mobile">
            <img
              src={upload_image}
              alt="Share listing"
              className="share_listing"
            />

            {userData?.user && userData?.user?.id ? (
              <button
                ref={heartRef}
                onClick={handleFavorite}
                aria-label="Toggle Favorite"
                name="Toggle Favorite"
                type="button"
                className={`heart-span-${data?.mls_listing?.id} ${
                  favorited ? 'favorited' : ''
                } heart-toggle-${data?.mls_listing?.id}`}
              >
                {favorited ? (
                  <img
                    src={heart_filled_image}
                    alt="Favorite listing"
                    className="heart-icon"
                  />
                ) : (
                  <img
                    src={heart_path_image}
                    alt="Favorited listing"
                    className="heart-icon"
                  />
                )}
              </button>
            ) : (
              <a
                name="Toggle Favorite"
                aria-label="Toggle Favorite"
                type="button"
                className="save-prompt-toggle heart-icon"
              >
                <img
                  src={heart_path_image}
                  alt="Favorite listing"
                  className="heart-icon"
                />
              </a>
            )}

            {isModal && (
              <button
                type="button"
                onClick={closeModal}
                title="Close Modal"
                className="mls-modal-close"
              >
                <img src={close_image} alt="Close modal" />
              </button>
            )}
          </div>
        </section>

        <section className="mls-data-wrapper">
          <main className="mls-data-container">
            {isMobile && <MLSBreadcrumbs listing={data?.mls_listing} />}

            {isModal && (
              <button
                type="button"
                onClick={closeModal}
                title="Close Modal"
                className="mls-modal-close desktop"
              >
                <ReactSVG src={close} />
              </button>
            )}

            <section className="mls-price-container">
              <h2 className="mls-list-price">
                {formatCurrency(
                  data?.mls_listing?.sold_price ?? data?.mls_listing?.list_price
                )}
              </h2>

              <div className="mls-actions desktop">
                <button
                  ref={heartRef}
                  type="button"
                  name="Share Listing"
                  aria-label="Share Listing"
                  className="btn btn--small btn--light share share_listing"
                >
                  <ReactSVG src={upload_alt} />
                  <span>Share</span>
                </button>

                {userData?.user && userData?.user?.id ? (
                  <button
                    ref={heartRef}
                    onClick={handleFavorite}
                    aria-label="Toggle Favorite"
                    name="Toggle Favorite"
                    type="button"
                    className={`btn btn--small btn--light ${
                      favorited ? 'favorited' : ''
                    } heart-toggle-${data?.mls_listing?.id}`}
                  >
                    <span
                      className={`save heart-span-${data?.mls_listing?.id}`}
                    >
                      <ReactSVG src={heart_path} />
                      <span>{favorited ? 'Saved' : 'Save'}</span>
                    </span>
                  </button>
                ) : (
                  <span
                    role="presentation"
                    className={`save-prompt-toggle btn btn--small btn--light save heart-span-${data?.mls_listing?.id}`}
                    onClick={() => {
                      const modal = document.querySelector('#popup-modal');
                      modal.classList.remove('fadeOut');
                      modal.classList.remove('hidden');
                      modal.classList.add('fadeIn');
                      disableBodyScroll(modal);
                    }}
                  >
                    <ReactSVG src={heart_2d} className="lazyload" />
                    <span>Save</span>
                  </span>
                )}
              </div>
            </section>

            <h1 className="mls-address">{data?.mls_listing?.address}</h1>

            {data?.mls_listing?.subdivision && (
              <p className="mls-subdivision">
                {data?.mls_listing?.subdivision}
              </p>
            )}

            {data?.mls_listing?.listing_status && (
              <p className="mls-status-wrapper">
                <span
                  className={`mls-status ${data?.mls_listing?.listing_status
                    ?.toLowerCase()
                    ?.replace(/\s/g, '-')}`}
                />
                <span className="mls-status-text">
                  {
                    mls_statuses[
                      isRental
                        ? `${data?.mls_listing?.listing_status}-Rental`
                        : data?.mls_listing?.listing_status
                    ]
                  }
                </span>
                {data?.mls_listing?.sold_date && (
                  <span className="mls-status-text">
                    <span style={{ margin: '0 2px' }}>|</span> Sold on {''}
                    {format(data?.mls_listing?.sold_date, 'MM/DD/YY')}
                  </span>
                )}
              </p>
            )}

            <section className="mls-primary-details-container">
              {data?.mls_listing?.sub_type && (
                <div className="mls-primary-detail">
                  <ReactSVG src={home} />
                  <p>
                    {subType
                      ? subType?.short_label
                      : data?.mls_listing?.sub_type}
                  </p>
                </div>
              )}

              {data?.mls_listing?.total_bedrooms !== 0 && (
                <div className="mls-primary-detail">
                  <ReactSVG src={bed} />
                  <p>{data?.mls_listing?.total_bedrooms} Bed</p>
                </div>
              )}

              {data?.mls_listing?.total_baths !== 0 && (
                <div className="mls-primary-detail">
                  <ReactSVG src={shower} />
                  <p>{data?.mls_listing?.total_baths} Bath</p>
                </div>
              )}

              {data?.mls_listing?.apx_sqft !== 0 && (
                <div className="mls-primary-detail">
                  <ReactSVG src={sqft} />
                  <p>{data?.mls_listing?.apx_sqft} sqft</p>
                </div>
              )}
            </section>

            <MLSShowMap
              coords={{
                lat: data?.mls_listing?.latitude,
                lng: data?.mls_listing?.longitude,
              }}
            />

            <DescriptionTruncate description={data?.mls_listing?.description} />

            <AgentContact
              agent={data?.mls_listing?.agent}
              pushDataInToDataLayer={pushDataInToDataLayer}
            />

            <section className="mls-source-data">
              <div className="mls-source-data-container">
                <p className="mls-data-label">Last Updated</p>
                <p className="mls-data-value">
                  {format(data?.mls_listing?.updated_at, 'MMMM DD, YYYY') ||
                    'N/A'}
                </p>
              </div>

              <div className="mls-source-data-container popup-form">
                <p className="mls-data-label">Days on Market</p>
                <p className="mls-data-value">
                  {data?.mls_listing?.days_on_market
                    ? `${data?.mls_listing?.days_on_market} Day${
                        data?.mls_listing?.days_on_market === '1' ? '' : 's'
                      }`
                    : 'N/A'}
                </p>
              </div>

              <div className="mls-source-data-container">
                <p className="mls-data-label">Listing Office</p>
                <p className="mls-data-value">
                  {data?.mls_listing?.listing_office_name || 'N/A'}
                </p>
              </div>

              <div className="mls-source-data-container">
                <p className="mls-data-label">Originating MLS</p>
                <p className="mls-data-value">
                  {data?.mls_listing?.listing_board || 'N/A'}
                </p>
              </div>

              <div className="mls-source-data-container">
                <p className="mls-data-label">MLS#</p>
                <p className="mls-data-value">
                  {data?.mls_listing?.listing_id || 'N/A'}
                </p>
              </div>
            </section>

            {placards && (
              <section>
                <AdBlock placards={placards} showTwo />
              </section>
            )}

            <section className="mls-data-section amenities">
              <h3 className="mls-data-section-title">Property Details</h3>

              {(data?.mls_listing?.total_bedrooms ||
                data?.mls_listing?.total_baths) && (
                <aside className="mls-amenities-section">
                  <p className="mls-amenities-section-title">
                    Bedrooms & Bathrooms
                  </p>
                  <div className="mls-amenities-grid">
                    <p>Bathrooms: {data?.mls_listing?.total_baths}</p>
                    <p>Full Baths: {data?.mls_listing?.full_baths}</p>
                    <p>Half Baths: {data?.mls_listing?.half_baths}</p>
                    <p>Bedrooms: {data?.mls_listing?.total_bedrooms}</p>
                  </div>
                </aside>
              )}

              {data?.mls_listing?.interior && (
                <aside className="mls-amenities-section">
                  <p className="mls-amenities-section-title">
                    Interior Features
                  </p>
                  <div className="mls-amenities-grid">
                    {data?.mls_listing?.interior?.map((i, ind) => (
                      <p key={ind}>{i}</p>
                    ))}
                  </div>
                </aside>
              )}

              {data?.mls_listing?.appliances && (
                <aside className="mls-amenities-section">
                  <p className="mls-amenities-section-title">Appliances</p>
                  <div className="mls-amenities-grid">
                    {data?.mls_listing?.appliances?.map((a, ind) => (
                      <p key={ind}>{a}</p>
                    ))}
                  </div>
                </aside>
              )}

              {data?.mls_listing?.rooms && (
                <aside className="mls-amenities-section">
                  <p className="mls-amenities-section-title">Other Rooms</p>
                  <div className="mls-amenities-grid">
                    {data?.mls_listing?.rooms?.map((r, ind) => (
                      <p key={ind}>{r}</p>
                    ))}
                  </div>
                </aside>
              )}

              {data?.mls_listing?.exterior && (
                <aside className="mls-amenities-section">
                  <p className="mls-amenities-section-title">
                    Exterior Features
                  </p>
                  <div className="mls-amenities-grid">
                    {data?.mls_listing?.exterior?.map((e, ind) => (
                      <p key={ind}>{e}</p>
                    ))}
                  </div>
                </aside>
              )}

              {data?.mls_listing?.exterior && (
                <aside className="mls-amenities-section">
                  <p className="mls-amenities-section-title">Parking</p>
                  <div className="mls-amenities-grid">
                    <p>
                      Carport Spaces: {data?.mls_listing?.carport_spaces || 0}
                    </p>
                    <p>
                      Garage Spaces: {data?.mls_listing?.garage_spaces || 0}
                    </p>
                    <p>
                      Street Spaces: {data?.mls_listing?.street_spaces || 0}
                    </p>
                    <p>
                      Covered Spaces: {data?.mls_listing?.covered_spaces || 0}
                    </p>
                    <p>
                      Parking features:{' '}
                      {data?.mls_listing?.parking_features?.length > 0
                        ? data?.mls_listing?.parking_features?.join(', ')
                        : 'N/A'}
                    </p>
                  </div>
                </aside>
              )}

              <aside className="mls-amenities-section">
                <p className="mls-amenities-section-title">Lot Details</p>
                <div className="mls-amenities-grid col-1">
                  {data?.mls_listing?.acreage && (
                    <p>Lot Size: {data?.mls_listing?.acreage} Acres</p>
                  )}
                  <p>Lot Dimensions: {data?.mls_listing?.lot_dimensions}</p>
                  {data?.mls_listing?.lot_features?.length > 0 && (
                    <p>
                      Lot Features:{' '}
                      {data?.mls_listing?.lot_features?.join(', ')}
                    </p>
                  )}
                </div>
              </aside>

              <aside className="mls-amenities-section">
                <p className="mls-amenities-section-title">
                  Other Property Details
                </p>
                <div className="mls-amenities-grid">
                  <p>Parcel Number: {data?.mls_listing?.tax_id || 'N/A'}</p>
                  <p>
                    Zoning:{' '}
                    {data?.mls_listing?.zoning
                      ? data?.mls_listing?.zoning?.join(', ')
                      : 'N/A'}
                  </p>
                  <p>
                    Water View:{' '}
                    {data?.mls_listing?.waterview
                      ? data?.mls_listing?.waterview?.join(', ')
                      : 'N/A'}
                  </p>
                  <p>
                    Annual Tax Amount: {data?.mls_listing?.last_taxes || 'N/A'}
                  </p>
                  <p>County: {data?.mls_listing?.county || 'N/A'}</p>
                  <p>Tax Year: {data?.mls_listing?.last_tax_year || 'N/A'}</p>
                  <p>Area: {data?.mls_listing?.mls_area || 'N/A'}</p>
                  <p>
                    Source Listing Status:{' '}
                    {data?.mls_listing?.listing_status || 'N/A'}
                  </p>
                </div>
              </aside>

              {data?.mls_listing?.construction_status && (
                <aside className="mls-amenities-section">
                  <p className="mls-amenities-section-title">
                    Construction Details
                  </p>
                  <div className="mls-amenities-grid col-2">
                    <p>
                      Total SqFt Living: {data?.mls_listing?.apx_sqft || 'N/A'}
                    </p>
                    <p>Year Built: {data?.mls_listing?.year_built || 'N/A'}</p>
                    <p>
                      Building Exterior Type:{' '}
                      {data?.mls_listing?.construction_siding?.join(', ') ||
                        'N/A'}
                    </p>
                    <p>
                      Year Built Details:{' '}
                      {data?.mls_listing?.construction_status?.join(', ') ||
                        'N/A'}
                    </p>
                    <p>House Style: {data?.mls_listing?.design || 'N/A'}</p>
                    <p>
                      Levels/Stories: {data?.mls_listing?.num_stories || 'N/A'}
                    </p>
                  </div>
                </aside>
              )}

              {data?.mls_listing?.energy && (
                <aside className="mls-amenities-section">
                  <p className="mls-amenities-section-title">
                    Energy & Utilities
                  </p>
                  <div className="mls-amenities-grid">
                    {data?.mls_listing?.energy?.map((e, ind) => (
                      <p key={ind}>{e}</p>
                    ))}
                    <p>
                      Energy: {data?.mls_listing?.energy.join(', ') || 'N/A'}
                    </p>
                  </div>
                </aside>
              )}

              <aside className="mls-amenities-section">
                <p className="mls-amenities-section-title">
                  Home Owners Association
                </p>
                <div className="mls-amenities-grid col-2">
                  <p>
                    Association: {data?.mls_listing?.assessment_fee || 'N/A'}
                  </p>
                  <p>
                    Association Fee:{' '}
                    {data?.mls_listing?.assessment_amount || 'N/A'}
                  </p>
                  <p>
                    Association Fee Frequency:{' '}
                    {data?.mls_listing?.assessment_paid || 'N/A'}
                  </p>
                  <p>
                    Calculated Total Monthly Association Fees:{' '}
                    {data?.mls_listing?.calculated_monthly_assesment_fees ||
                      'N/A'}
                  </p>
                  <p>
                    Association Fee Includes:{' '}
                    {data?.mls_listing?.fees_include?.length > 1
                      ? data?.mls_listing?.fees_include?.join(', ')
                      : 'N/A'}
                  </p>
                </div>
              </aside>
            </section>

            <hr className="section-divider" />

            <section className="mls-data-section area">
              <div className="schools">
                <h3 className="mls-data-section-title">Schools</h3>
                <p>Elementary School: {data?.mls_listing?.elementary_school}</p>
                <p>Middle School: {data?.mls_listing?.middle_school}</p>
                <p>High School: {data?.mls_listing?.high_school}</p>
              </div>

              <div className="neighborhood">
                <h3 className="mls-data-section-title">Neighborhood</h3>
                <p>
                  {shortAddress()} is located in the{' '}
                  <span>{data?.mls_listing?.sub_area?.name}</span> neighborhood
                  of{' '}
                  <span>
                    {data?.mls_listing?.city}, {data?.mls_listing?.state}
                  </span>
                  .
                </p>

                <section className="average-prices">
                  <div>
                    <h4>
                      {data?.mls_listing?.sub_area?.median_list_price
                        ? formatCurrency(
                            data?.mls_listing?.sub_area?.median_list_price
                          )
                        : 'N/A'}
                    </h4>
                    <p>Median Listing Price</p>
                  </div>
                  <div>
                    <h4>
                      {data?.mls_listing?.sub_area?.median_price_per_sqft
                        ? formatCurrency(
                            data?.mls_listing?.sub_area?.median_price_per_sqft
                          )
                        : 'N/A'}
                    </h4>
                    <p>Price Per Sq Ft</p>
                  </div>
                </section>
              </div>
            </section>

            <hr className="section-divider" />

            {/* <%# react_component 'MLS/SimilarProperties' %> */}

            <ListMyHome
              data={data?.mls_listing}
              current_user={userData?.user}
              areas={areas}
            />

            <section className="mls-data-section area">
              <div className="additional-resources">
                <h3 className="mls-data-section-title">Additional Resources</h3>
                <p className="real-estate">
                  Search For More{' '}
                  <a
                    href="https://destinflorida.com/real-estate"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Destin Florida Real Estate
                  </a>
                </p>
                <div className="be-ix-link-block" />
              </div>
            </section>
          </main>
        </section>
      </div>
      {ReactDOM.createPortal(
        <InquiryForm
          agent={data?.mls_listing?.agent}
          property={data?.mls_listing}
          current_user={userData?.user}
          pushDataInToDataLayer={pushDataInToDataLayer}
        />,
        document.querySelector('#mls-inquiry-modal-portal')
      )}
      {ReactDOM.createPortal(
        <ShareModal
          subject="Check out this property on DestinFlorida.com!"
          body={`Hi! I found some info about ${data?.mls_listing?.address} on DestinFlorida.com and thought you might want to know about it.`}
          copyLink={`${window.location.origin}/for-sale/${data?.mls_listing?.address_show_url}`}
        />,
        document.querySelector('#mls-inquiry-modal-portal')
      )}
    </Fragment>
  );
}
